import React, { useState, useEffect, useContext } from 'react';
import ImageCacheContext from './ImageCacheContext';
import { bigLogo } from '../Icons';

const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open("ImageCacheDB", 1);

    request.onerror = (event) => {
      console.error("Database error:", event.target.errorCode);
      reject(event.target.errorCode);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore("images", { keyPath: "id" });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
};

const addImageToCacheIndexedDB = async (id, imageData) => {
  const db = await openDatabase();
  const transaction = db.transaction(["images"], "readwrite");
  const store = transaction.objectStore("images");
  store.put({ id, ...imageData });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

const getImageFromCacheIndexedDB = async (id) => {
  const db = await openDatabase();
  const transaction = db.transaction(["images"], "readonly");
  const store = transaction.objectStore("images");
  const request = store.get(id);

  return new Promise((resolve, reject) => {
    request.onerror = () => {
      reject(null);
    };
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
};

const CachedImage = ({ userId, src, previewImage, alt, ...props }) => {
  const { addImageToCache, getImageFromCache } = useContext(ImageCacheContext);
  const [imageSrc, setImageSrc] = useState(previewImage || bigLogo);

  useEffect(() => {
    let isMounted = true;
    const currentTime = Date.now();

    const loadImage = async () => {
      try {
        const cachedImage = await getImageFromCacheIndexedDB(userId);
        if (cachedImage && currentTime - cachedImage.timestamp < 9000000) {
          if (isMounted) setImageSrc(cachedImage.data);
        } else {
          if (!src) return;
          const response = await fetch(src);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64data = reader.result;
            await addImageToCacheIndexedDB(userId, { data: base64data, timestamp: currentTime });
            if (isMounted) setImageSrc(base64data);
          };
          reader.readAsDataURL(blob);
        }
      } catch (error) {
        console.error("Error using IndexedDB, falling back to original caching method.", error);
        const cachedImage = getImageFromCache(userId);
        if (cachedImage) {
          if (isMounted) setImageSrc(cachedImage);
        } else {
          if (src) {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              addImageToCache(userId, src);
              if (isMounted) setImageSrc(src);
            };
            img.onerror = () => {
              if (isMounted) setImageSrc(bigLogo);
            };
          }
        }
      }
    };
    loadImage();
    return () => {
      isMounted = false;
    };
  }, [userId, src, addImageToCache, getImageFromCache]);

  return (
    <img src={imageSrc} alt={alt || ''} onError={(e) => { e.target.onerror = null; e.target.src = bigLogo; }} {...props} />
  );
};

export default CachedImage;